import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import libraryService from '../../services/libraryService';
import LibraryPreviewModal from './LibraryPreviewModal';
import { BookOpen, PlayCircle, MagnifyingGlass, Star, Heart, PencilSimple, Trash } from '@phosphor-icons/react';

const MATERIAL_FILTERS = [
  { value: 'all', label: 'All materials' },
  { value: 'book', label: 'Books' },
  { value: 'textbook', label: 'Textbooks' },
  { value: 'academic_material', label: 'Academic Materials' },
  { value: 'educational_video', label: 'Educational Videos' },
  { value: 'worksheet', label: 'Worksheets' },
  { value: 'reference', label: 'Reference' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'views', label: 'Most viewed' },
  { value: 'downloads', label: 'Most downloaded' },
  { value: 'rating', label: 'Top rated' },
];

const backendBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') || 'http://127.0.0.1:8000';

function buildUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${backendBase}${path.startsWith('/') ? path : `/${path}`}`;
}

function LibraryCatalog({
  categories = [],
  tags = [],
  extraParams = {},
  showManageActions = false,
  canManage = () => false,
  onEdit,
  onDelete,
  refreshKey = 0,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [materialType, setMaterialType] = useState('all');
  const [categoryId, setCategoryId] = useState('');
  const [tagId, setTagId] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [previewItem, setPreviewItem] = useState(null);

  const extraParamsKey = JSON.stringify(extraParams);

  useEffect(() => {
    setPage(1);
  }, [search, materialType, categoryId, tagId, sortBy, extraParamsKey]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: 12,
          sort_by: sortBy,
          search: search || undefined,
          material_type: materialType === 'all' ? undefined : materialType,
          category_id: categoryId || undefined,
          tag_id: tagId || undefined,
          ...extraParams,
        };
        const data = await libraryService.getItems(params);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Unable to load library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, materialType, categoryId, tagId, sortBy, page, refreshKey, extraParamsKey]);

  const refresh = () => setPage((p) => p);

  const handleReserve = async (item) => {
    try {
      await libraryService.createReservation({ item_id: item.id });
      toast.success('Reserved — you will be able to borrow it once a copy is returned');
    } catch (error) {
      toast.error(error.message || 'Unable to reserve this title');
    }
  };

  const handleDelete = async (item) => {
    if (!onDelete) return;
    onDelete(item);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
            <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: 11 }} />
            <input
              className="form-control"
              style={{ paddingLeft: 36 }}
              placeholder="Search books, notes or videos"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="form-control" value={materialType} onChange={(e) => setMaterialType(e.target.value)}>
            {MATERIAL_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          {categories.length > 0 && (
            <select className="form-control" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {tags.length > 0 && (
            <select className="form-control" value={tagId} onChange={(e) => setTagId(e.target.value)}>
              <option value="">All tags</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <select className="form-control" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="empty-state">Loading e-library...</div>
        ) : items.length === 0 ? (
          <div className="empty-state">No materials are available yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {items.map((item) => (
              <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: item.material_type === 'educational_video' ? '#dbeafe' : '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {item.material_type === 'educational_video' ? <PlayCircle size={22} color="#2563eb" /> : <BookOpen size={22} color="#16a34a" />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{item.title}</div>
                      <div style={{ color: '#6b7280', fontSize: 13 }}>
                        {[item.author, item.publisher].filter(Boolean).join(' · ') || item.description || 'No description'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        {item.average_rating != null && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#6b7280' }}>
                            <Star size={13} weight="fill" color="#f59e0b" /> {item.average_rating.toFixed(1)} ({item.ratings_count})
                          </span>
                        )}
                        {item.is_favorited && <Heart size={13} weight="fill" color="#dc2626" />}
                        {item.copies_total > 0 && (
                          <span className="badge">{item.copies_available} of {item.copies_total} copies available</span>
                        )}
                        {item.tags?.slice(0, 3).map((tag) => <span key={tag} className="badge">{tag}</span>)}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div className="badge">{item.material_type}</div>
                    {item.is_featured && <div className="badge">Featured</div>}
                    {showManageActions && !item.is_published && <div className="badge">Draft</div>}
                  </div>
                </div>

                {item.thumbnail_url && (
                  <img src={buildUrl(item.thumbnail_url)} alt={item.title} style={{ width: '100%', borderRadius: 12, objectFit: 'cover', maxHeight: 200 }} />
                )}

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(item.file_url || item.external_url) ? (
                    <button className="btn btn-secondary" type="button" onClick={() => setPreviewItem(item)}>Preview</button>
                  ) : item.copies_total === 0 ? (
                    <span className="empty-state">No resource attached</span>
                  ) : null}
                  {item.copies_total > 0 && item.copies_available === 0 && (
                    <button className="btn btn-outline" type="button" onClick={() => handleReserve(item)}>Reserve</button>
                  )}
                  {showManageActions && canManage(item) && (
                    <>
                      <button className="btn btn-secondary" type="button" onClick={() => onEdit && onEdit(item)} title="Edit">
                        <PencilSimple size={16} />
                      </button>
                      <button className="btn btn-danger" type="button" onClick={() => handleDelete(item)} title="Delete">
                        <Trash size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-secondary" type="button" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page} of {pages} ({total} total)</span>
            <button className="btn btn-secondary" type="button" disabled={page === pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {previewItem && (
        <LibraryPreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

export default LibraryCatalog;
